/**
 * Purpose: `useRealtimeTalk` — the in-chat OpenAI Realtime voice session
 *          behind the **Talk** button when the widget runs with
 *          `voice="realtime"`. Owns a `RealtimeDriver`, turns its events
 *          into chat turns (via `TalkTurnSequencer`) and exposes a small
 *          state surface (phase, live reply text, tool label) for the
 *          composer's voice bar.
 *
 * Implementation notes:
 *   - Opt-in only. `<Copilot>` calls this hook unconditionally (Rules of
 *     Hooks) but nothing connects unless the host chose `voice="realtime"`
 *     and the user pressed Talk. The default `voice="browser"` path and
 *     the legacy `realtime` voice-only panel never touch it.
 *   - The session gets the chat's own system prompt plus spoken-style
 *     rules and the chat history so far (see `buildRealtimeTalkInstructions`
 *     in @ada/copilot-core), the same client tools and server tools, and
 *     user-side transcription so spoken turns land in the chat.
 *   - Barge-in is OpenAI's default server VAD (`interrupt_response: true`);
 *     nothing here mutes the mic while the assistant talks. Echo
 *     cancellation on the mic (driver default) is what keeps the
 *     assistant from interrupting itself on loudspeakers.
 *   - Options are read through a ref so the long-lived driver listener
 *     always sees the latest callbacks without reconnecting.
 *   - A 1 s ticker expires overdue user transcripts and ends the session
 *     after `idleTimeoutMs` without activity, so an open mic is never left
 *     billing in a forgotten tab.
 *
 * Created: 2026-10-06 17:00
 *
 * Changelog:
 *   - 2026-10-06 17:00 — Initial version. In-chat realtime voice for the
 *     opt-in `voice="realtime"` mode (backward compatible: default modes
 *     unchanged).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClientToolRegistry, RealtimeDriver, TalkTurnSequencer, buildRealtimeTalkInstructions, } from '@ada/copilot-core';
/** Default OpenAI voice for the in-chat mode (best quality GA voice). */
export const DEFAULT_TALK_VOICE = 'marin';
/** Default user-side transcription model (an OpenAI id, not an ada alias). */
export const DEFAULT_TALK_TRANSCRIPTION_MODEL = 'gpt-4o-mini-transcribe';
/** Default inactivity cut-off. */
export const DEFAULT_TALK_IDLE_TIMEOUT_MS = 120_000;
/** OpenAI error codes that are expected races, not failures. */
const BENIGN_SERVER_ERRORS = new Set([
    'response_cancel_not_active',
    'conversation_already_has_active_response',
]);
export function realtimeTalkSupported() {
    return (typeof globalThis.RTCPeerConnection !== 'undefined' &&
        typeof navigator !== 'undefined' &&
        typeof navigator.mediaDevices?.getUserMedia === 'function');
}
export function useRealtimeTalk(opts) {
    const optsRef = useRef(opts);
    optsRef.current = opts;
    const [phase, setPhase] = useState('idle');
    const [liveText, setLiveText] = useState('');
    const [toolLabel, setToolLabel] = useState(null);
    const driverRef = useRef(null);
    const seqRef = useRef(null);
    const tickerRef = useRef(null);
    const lastActivityRef = useRef(0);
    // Set while we tear the session down ourselves, so the driver's own
    // 'closed' status isn't reported as a dropped connection.
    const endingRef = useRef(false);
    // Whether this session reports WebRTC playback events; without them the
    // transcript is the only "speaking" signal.
    const audioEventsRef = useRef(false);
    const phaseRef = useRef('idle');
    const supported = useMemo(() => realtimeTalkSupported(), []);
    const updatePhase = useCallback((next) => {
        phaseRef.current = next;
        setPhase(next);
    }, []);
    const touch = useCallback(() => {
        lastActivityRef.current = Date.now();
    }, []);
    // Hand finished turns to the host and refresh the live bubble.
    const flush = useCallback(() => {
        const seq = seqRef.current;
        if (!seq)
            return;
        const turns = seq.drain();
        if (turns.length > 0)
            optsRef.current.onTurns(turns);
        setLiveText(seq.liveAssistantText);
    }, []);
    const teardown = useCallback((commit) => {
        const driver = driverRef.current;
        const seq = seqRef.current;
        driverRef.current = null;
        seqRef.current = null;
        if (tickerRef.current) {
            clearInterval(tickerRef.current);
            tickerRef.current = null;
        }
        if (seq && commit) {
            const turns = seq.flush();
            if (turns.length > 0)
                optsRef.current.onTurns(turns);
        }
        if (driver) {
            endingRef.current = true;
            void driver.disconnect().finally(() => {
                endingRef.current = false;
            });
        }
        updatePhase('idle');
        setLiveText('');
        setToolLabel(null);
    }, [updatePhase]);
    const end = useCallback(() => teardown(true), [teardown]);
    const fail = useCallback((message) => {
        teardown(true);
        optsRef.current.onError({ kind: 'dropped', message });
    }, [teardown]);
    const handleEvent = useCallback((driver, event) => {
        // Events from a driver we already replaced or ended.
        if (driverRef.current !== driver)
            return;
        const seq = seqRef.current;
        if (!seq)
            return;
        const o = optsRef.current;
        switch (event.type) {
            case 'speech-started':
                touch();
                updatePhase('listening');
                return;
            case 'input-committed':
                touch();
                seq.userCommitted(event.itemId);
                updatePhase('thinking');
                return;
            case 'transcript':
                touch();
                if (event.role === 'user') {
                    seq.userTranscript(event.itemId, event.delta);
                }
                else if (event.final) {
                    seq.assistantDone(event.itemId, event.delta, event.responseId);
                }
                else {
                    seq.assistantDelta(event.itemId, event.delta, event.responseId);
                    setToolLabel(null);
                    if (!audioEventsRef.current)
                        updatePhase('speaking');
                }
                flush();
                return;
            case 'output-audio':
                touch();
                seq.assistantAudio(event.state, event.responseId);
                if (event.state === 'started') {
                    audioEventsRef.current = true;
                    setToolLabel(null);
                    updatePhase('speaking');
                }
                else if (phaseRef.current === 'speaking') {
                    updatePhase('listening');
                }
                flush();
                return;
            case 'response-done': {
                seq.responseDone(event.status, event.responseId);
                const p = phaseRef.current;
                if (p === 'thinking' || (p === 'speaking' && !audioEventsRef.current)) {
                    updatePhase('listening');
                }
                flush();
                return;
            }
            case 'tool-call':
                touch();
                setToolLabel(o.toolLabels.get(event.name) ?? o.genericToolLabel);
                updatePhase('tool');
                return;
            case 'tool-result':
                touch();
                setToolLabel(null);
                updatePhase('thinking');
                return;
            case 'status':
                // While connecting, failures surface through connect()'s
                // rejection instead (see start()). Afterwards, 'closed' means the
                // datachannel dropped.
                if (phaseRef.current === 'connecting')
                    return;
                if (event.status === 'closed' && !endingRef.current) {
                    fail('datachannel closed');
                }
                return;
            case 'error': {
                if (phaseRef.current === 'connecting' && event.code !== 'autoplay-blocked')
                    return;
                if (event.code === 'peer-connection') {
                    // 'disconnected' can recover on its own; 'failed' / 'closed' can't.
                    if (!/disconnected/.test(event.message))
                        fail(event.message);
                    return;
                }
                if (event.code && BENIGN_SERVER_ERRORS.has(event.code))
                    return;
                if (event.code === 'usage-report' || event.code === 'parse') {
                    console.warn('[ada-copilot] realtime:', event.message);
                    return;
                }
                o.onError({ kind: 'session', message: event.message });
                return;
            }
            default:
                return;
        }
    }, [fail, flush, touch, updatePhase]);
    const start = useCallback(async () => {
        if (driverRef.current || !supported)
            return;
        const o = optsRef.current;
        const cfg = o.config ?? {};
        const registry = new ClientToolRegistry();
        for (const t of o.tools ?? [])
            registry.register(t);
        let instructions = buildRealtimeTalkInstructions({
            basePrompt: o.basePrompt,
            history: o.getHistory(),
        });
        if (cfg.instructions)
            instructions += `\n\n${cfg.instructions}`;
        const audioInput = {
            ...(cfg.audioInput ?? {}),
            transcription: cfg.audioInput?.transcription ?? {
                model: DEFAULT_TALK_TRANSCRIPTION_MODEL,
                ...(o.transcriptionLanguage ? { language: o.transcriptionLanguage } : {}),
            },
        };
        const audioOutput = {
            voice: cfg.voice ?? DEFAULT_TALK_VOICE,
            ...(cfg.audioOutput ?? {}),
        };
        const maxOutputTokens = cfg.maxOutputTokens ?? cfg.maxResponseOutputTokens;
        const driver = new RealtimeDriver({
            llm: o.llm,
            toolRegistry: registry,
            ...(o.serverTools !== undefined ? { serverTools: o.serverTools } : {}),
            model: cfg.model ?? 'realtime',
            instructions,
            audioInput,
            audioOutput,
            ...(cfg.toolChoice !== undefined ? { toolChoice: cfg.toolChoice } : {}),
            ...(cfg.outputModalities !== undefined ? { outputModalities: cfg.outputModalities } : {}),
            ...(cfg.temperature !== undefined ? { temperature: cfg.temperature } : {}),
            ...(maxOutputTokens !== undefined ? { maxOutputTokens } : {}),
            ...(cfg.tracing !== undefined ? { tracing: cfg.tracing } : {}),
            ...(cfg.prompt !== undefined ? { prompt: cfg.prompt } : {}),
            ...(cfg.include !== undefined ? { include: cfg.include } : {}),
            ...(cfg.sessionOverrides !== undefined ? { sessionOverrides: cfg.sessionOverrides } : {}),
            ...(cfg.expiresInSeconds !== undefined ? { expiresInSeconds: cfg.expiresInSeconds } : {}),
        });
        driverRef.current = driver;
        seqRef.current = new TalkTurnSequencer();
        audioEventsRef.current = false;
        endingRef.current = false;
        driver.on((event) => handleEvent(driver, event));
        touch();
        updatePhase('connecting');
        try {
            await driver.connect();
        }
        catch (err) {
            // `connect()` already tore the driver down; just report it.
            if (driverRef.current === driver) {
                driverRef.current = null;
                seqRef.current = null;
                updatePhase('idle');
                optsRef.current.onError({ kind: 'start', message: err.message });
            }
            return;
        }
        if (driverRef.current !== driver)
            return; // ended while connecting
        updatePhase('listening');
        tickerRef.current = setInterval(() => {
            const seq = seqRef.current;
            if (!seq || driverRef.current !== driver)
                return;
            const now = Date.now();
            seq.expire(now);
            flush();
            const idleMs = optsRef.current.idleTimeoutMs ?? DEFAULT_TALK_IDLE_TIMEOUT_MS;
            // Never while a reply plays (a long answer sends no events until it
            // ends); a session stuck anywhere else with no events is idle.
            const p = phaseRef.current;
            if (p !== 'speaking' &&
                p !== 'connecting' &&
                !seq.hasPending &&
                now - lastActivityRef.current > idleMs) {
                teardown(true);
                optsRef.current.onIdleEnd?.();
            }
        }, 1000);
    }, [supported, handleEvent, touch, updatePhase, flush, teardown]);
    const interrupt = useCallback(() => {
        driverRef.current?.interrupt();
    }, []);
    const sendText = useCallback((text) => {
        const driver = driverRef.current;
        const seq = seqRef.current;
        if (!driver || !seq || !text.trim())
            return;
        touch();
        seq.userTyped(text);
        flush();
        driver.sendText(text);
        updatePhase('thinking');
    }, [flush, touch, updatePhase]);
    // Push client-tool changes to the live session, like <VoiceWidget>.
    useEffect(() => {
        driverRef.current?.updateClientTools(opts.tools ?? []);
    }, [opts.tools]);
    // Unmount: hang up without committing (the component is going away).
    useEffect(() => () => teardown(false), [teardown]);
    return {
        supported,
        active: phase !== 'idle',
        phase,
        liveText,
        toolLabel,
        start,
        end,
        interrupt,
        sendText,
    };
}
//# sourceMappingURL=realtime-talk.js.map